/**
 * Browser sign-in, shared by every front end.
 *
 * WHY THIS IS A PACKAGE AND NOT A FILE IN EACH APP.
 *
 * There were two copies of this flow — `apps/clinician-web/src/lib/auth.ts` and
 * `tools/smoke/lib/sign-in.mjs` — and they drifted. The smoke copy passed a
 * `clientId` the app did not, and a deploy failed with "User not found" on the
 * check whose entire purpose was to prove a clinician can work. The comment
 * written at the time still applies: *a duplicated authentication flow is a
 * duplicated set of assumptions about a server neither copy controls.*
 *
 * The patient portal needs the same flow with a different `clientId` rule, so
 * a third copy would be a third set of assumptions. One copy.
 *
 * CONFIGURED, NOT AMBIENT. The two apps run on different hosts with different
 * clients, so nothing here reads `import.meta.env`. `createAuth` takes what it
 * needs and each app supplies it — which also makes every branch testable
 * without a bundler.
 *
 * The flow is Medplum's: `POST /auth/login` returns an authorization code,
 * exchanged at `POST /oauth2/token`. There is no password grant. PKCE uses
 * **S256** rather than `plain`: the browser has WebCrypto, so there is no
 * reason to send the verifier in the clear.
 *
 * WHERE THE TOKEN LIVES, and the honest limitation: `sessionStorage` is
 * per-tab and cleared when the tab closes, which is the right shape for a
 * clinical workstation. It is also reachable by any script on the page, so it
 * is XSS-exposed in principle. What bounds that: a CSP with no `unsafe-inline`
 * and no third-party script, and a profile that holds synthetic data only
 * (§4.5.4). The end state is §9.2's SMART on FHIR v2 in Phase 4, where the
 * token stops living in JavaScript at all. This is a Phase 0 arrangement and
 * should not outlive Phase 0.
 */
/** Refresh this long before expiry, so a slow request cannot straddle it. */
const REFRESH_MARGIN_MS = 60_000;
export class AuthError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AuthError';
    }
}
// ---------------------------------------------------------------- PKCE ------
const base64Url = (bytes) => {
    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
export const createVerifier = () => base64Url(crypto.getRandomValues(new Uint8Array(32)));
export const deriveChallenge = async (verifier) => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64Url(new Uint8Array(digest));
};
const profileRefOf = (profile) => {
    if (profile === undefined)
        return null;
    if (typeof profile === 'string')
        return profile;
    return profile.reference ?? null;
};
const toSession = (body, email) => {
    if (typeof body.access_token !== 'string' || body.access_token === '') {
        throw new AuthError('The authorization server returned no access token.');
    }
    return {
        accessToken: body.access_token,
        refreshToken: body.refresh_token,
        // Default to an hour if the server omits it, but always store an absolute
        // instant: a relative lifetime is useless after a page reload.
        expiresAt: Date.now() + (body.expires_in ?? 3600) * 1000,
        profileRef: profileRefOf(body.profile),
        email,
    };
};
export const createAuth = (config) => {
    const readSession = () => {
        // Anything malformed is null rather than a throw: a corrupt session should
        // send the user to the login screen, not break the app shell.
        try {
            const raw = sessionStorage.getItem(config.sessionKey);
            if (raw === null)
                return null;
            const parsed = JSON.parse(raw);
            if (typeof parsed.accessToken !== 'string' || typeof parsed.expiresAt !== 'number') {
                return null;
            }
            return {
                accessToken: parsed.accessToken,
                refreshToken: parsed.refreshToken,
                expiresAt: parsed.expiresAt,
                profileRef: parsed.profileRef ?? null,
                email: parsed.email ?? '',
            };
        }
        catch {
            return null;
        }
    };
    const writeSession = (session) => {
        sessionStorage.setItem(config.sessionKey, JSON.stringify(session));
    };
    const signOut = () => {
        sessionStorage.removeItem(config.sessionKey);
    };
    const isExpired = (session, now = Date.now()) => now >= session.expiresAt - REFRESH_MARGIN_MS;
    const refresh = async (session) => {
        // Returns null when refresh is impossible or refused, which the caller
        // treats as "sign in again". A clinician logged out mid-shift because a
        // token hit sixty minutes is a real usability failure, so this runs ahead
        // of expiry rather than after it.
        if (session.refreshToken === undefined || session.refreshToken === '')
            return null;
        try {
            const response = await fetch(`${config.oauthBase}/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    grant_type: 'refresh_token',
                    refresh_token: session.refreshToken,
                }).toString(),
            });
            if (!response.ok)
                return null;
            const refreshed = toSession((await response.json()), session.email);
            writeSession(refreshed);
            return refreshed;
        }
        catch {
            return null;
        }
    };
    const signIn = async (email, password) => {
        // The error message is deliberately the same for a wrong password and an
        // unknown address: distinguishing them tells an attacker which addresses
        // exist.
        const verifier = createVerifier();
        const challenge = await deriveChallenge(verifier);
        const loginResponse = await fetch(`${config.authBase}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                // Omitted ENTIRELY when absent. Medplum reads a present-but-empty
                // clientId as "a project with no client", not as "unset", so sending
                // `clientId: ''` is a different request from sending nothing.
                ...(config.clientId === null || config.clientId === ''
                    ? {}
                    : { clientId: config.clientId }),
                email,
                password,
                codeChallengeMethod: 'S256',
                codeChallenge: challenge,
                // OIDC returns profile claims only when the `profile` scope is granted,
                // and the gateway needs one to know WHO is calling — with `openid`
                // alone Medplum's introspection returns a bare `sub`, and every
                // clinician was resolved as a machine (§1.3, §12.4, §14.2). §9.2
                // requires SMART on FHIR v2 scopes in any case; `openid` alone is not
                // one.
                scope: 'openid profile',
            }),
        });
        if (!loginResponse.ok) {
            throw new AuthError('Email or password is incorrect.');
        }
        const login = (await loginResponse.json());
        if (typeof login.code !== 'string' || login.code === '') {
            // A user in several projects gets `memberships` and no code, and must
            // pick one. Phase 0 is single-project; saying so beats a blank screen.
            if (Array.isArray(login.memberships) && login.memberships.length > 1) {
                throw new AuthError('This account belongs to more than one project. Project selection is not implemented ' +
                    'in Phase 0; use an account scoped to a single project.');
            }
            throw new AuthError('Sign-in did not return an authorization code.');
        }
        const tokenResponse = await fetch(`${config.oauthBase}/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'authorization_code',
                code: login.code,
                code_verifier: verifier,
            }).toString(),
        });
        if (!tokenResponse.ok) {
            throw new AuthError('Could not complete sign-in. Please try again.');
        }
        const session = toSession((await tokenResponse.json()), email);
        writeSession(session);
        return session;
    };
    const currentAccessToken = async () => {
        // Every request goes through this rather than reading storage directly, so
        // there is one place where expiry is handled.
        const session = readSession();
        if (session === null)
            return null;
        if (!isExpired(session))
            return session.accessToken;
        const refreshed = await refresh(session);
        if (refreshed !== null)
            return refreshed.accessToken;
        signOut();
        return null;
    };
    return { signIn, readSession, signOut, isExpired, refresh, currentAccessToken };
};
//# sourceMappingURL=auth.js.map